/**
 * Type Mappers
 * Convert between frontend types (camelCase) and backend DTOs (PascalCase)
 */

import type { WorkingPattern } from '@/types/timesheet';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import type { 
  OrganisationTimesheetSettings,
  TimesheetDeadlineSettings,
  HoursLimitSettings,
  UtilisationSettings,
  PolicyViolationSettings,
  PolicyCheckConfig,
  PolicyLimits,
  PolicyCheckType
} from '@/types/timesheetSettings';
import type {
  OrganisationTimesheetSettingsDto,
  TimesheetDeadlineSettingsDto,
  HoursLimitSettingsDto,
  UtilisationSettingsDto,
  PolicyViolationSettingsDto,
  WorkingPatternDto,
  EmployeeWorkingPatternDto,
  WorkingDaysDto,
  CreateWorkingPatternDto,
} from '@/types/api';


/**
 * Convert frontend WorkingPattern to backend WorkingDaysDto
 */
export function toWorkingDaysDto(workingDays: WorkingPattern['workingDays']): WorkingDaysDto {
  return {
    monday: workingDays.monday,
    tuesday: workingDays.tuesday,
    wednesday: workingDays.wednesday,
    thursday: workingDays.thursday,
    friday: workingDays.friday,
    saturday: workingDays.saturday,
    sunday: workingDays.sunday,
  };
}

/**
 * Convert backend WorkingDaysDto to frontend workingDays
 */
export function fromWorkingDaysDto(dto: WorkingDaysDto): WorkingPattern['workingDays'] {
  return {
    monday: dto.monday,
    tuesday: dto.tuesday,
    wednesday: dto.wednesday,
    thursday: dto.thursday,
    friday: dto.friday,
    saturday: dto.saturday,
    sunday: dto.sunday,
  };
}

/**
 * Convert frontend WorkingPattern to backend CreateWorkingPatternDto
 */
export function toCreateWorkingPatternDto(pattern: WorkingPattern): CreateWorkingPatternDto {
  return {
    hoursPerWeek: pattern.hoursPerWeek,
    workingDays: toWorkingDaysDto(pattern.workingDays),
    effectiveFrom: new Date().toISOString(),
    notes: pattern.notes || null,
  };
}

/**
 * Convert backend WorkingPatternDto to frontend WorkingPattern
 */
export function fromWorkingPatternDto(dto: WorkingPatternDto): WorkingPattern {
  return {
    hoursPerWeek: dto.hoursPerWeek,
    workingDays: fromWorkingDaysDto(dto.workingDays),
    notes: dto.notes || undefined,
  };
}

/**
 * Convert backend EmployeeWorkingPatternDto to frontend WorkingPattern with metadata
 */
export function fromEmployeeWorkingPatternDto(dto: EmployeeWorkingPatternDto): {
  pattern: WorkingPattern;
  inherited: boolean;
  id: string;
} {
  return {
    pattern: fromWorkingPatternDto(dto),
    inherited: dto.inherited,
    id: dto.id,
  };
}


/**
 * Convert frontend TimesheetDeadlineSettings to backend DTO
 */
export function toTimesheetDeadlineSettingsDto(
  settings: TimesheetDeadlineSettings
): TimesheetDeadlineSettingsDto {
  return {
    deadlineDay: settings.deadlineDay,
    deadlineTime: settings.deadlineTime,
    gracePeriodDays: settings.gracePeriodDays,
    sendReminders: settings.sendReminders,
    reminderDaysBefore: settings.reminderDaysBefore,
  };
}

/**
 * Convert backend TimesheetDeadlineSettingsDto to frontend
 */
export function fromTimesheetDeadlineSettingsDto(
  dto: TimesheetDeadlineSettingsDto
): TimesheetDeadlineSettings {
  return {
    deadlineDay: dto.deadlineDay as 0 | 1 | 2 | 3 | 4 | 5 | 6,
    deadlineTime: dto.deadlineTime,
    gracePeriodDays: dto.gracePeriodDays,
    sendReminders: dto.sendReminders,
    sendFeedReminders: DEFAULT_TIMESHEET_SETTINGS.deadline.sendFeedReminders,
    reminderDaysBefore: dto.reminderDaysBefore,
    enableCopyPreviousWeek: DEFAULT_TIMESHEET_SETTINGS.deadline.enableCopyPreviousWeek,
    copyDescriptions: DEFAULT_TIMESHEET_SETTINGS.deadline.copyDescriptions,
    copyOnlyBillable: DEFAULT_TIMESHEET_SETTINGS.deadline.copyOnlyBillable,
  };
}

/**
 * Convert frontend HoursLimitSettings to backend DTO
 */
export function toHoursLimitSettingsDto(
  settings: HoursLimitSettings
): HoursLimitSettingsDto {
  return {
    dailyHoursLimit: settings.dailyHoursLimit,
    weeklyHoursLimit: settings.weeklyHoursLimit,
    enforceHardLimits: settings.enforceHardLimits,
  };
}

/**
 * Convert backend HoursLimitSettingsDto to frontend
 */
export function fromHoursLimitSettingsDto(
  dto: HoursLimitSettingsDto
): HoursLimitSettings {
  return {
    dailyHoursLimit: dto.dailyHoursLimit,
    weeklyHoursLimit: dto.weeklyHoursLimit,
    enforceHardLimits: dto.enforceHardLimits,
  };
}

/**
 * Convert frontend UtilisationSettings to backend DTO
 */
export function toUtilisationSettingsDto(
  settings: UtilisationSettings
): UtilisationSettingsDto {
  return {
    targetUtilisationRate: settings.targetUtilisationRate,
    calculationMethod: settings.calculationMethod,
  };
}

/**
 * Convert backend UtilisationSettingsDto to frontend
 */
export function fromUtilisationSettingsDto(
  dto: UtilisationSettingsDto
): UtilisationSettings {
  return {
    targetUtilisationRate: dto.targetUtilisationRate,
    calculationMethod: dto.calculationMethod as 'billable-total' | 'billable-available' | 'billable-expected',
  };
}

/**
 * Convert frontend OrganisationTimesheetSettings to backend DTO
 */
export function toOrganisationTimesheetSettingsDto(
  settings: OrganisationTimesheetSettings,
  rowVersion?: string
): OrganisationTimesheetSettingsDto {
  // Legacy check keys that should not be persisted to the DB JSON
  const LEGACY_CHECK_KEYS: string[] = ['missing-hours', 'non-working-day'];

  // Convert policy violation checks to DTO format, filtering out legacy keys
  const checksDto = settings.policyViolations?.checks 
    ? Object.fromEntries(
        Object.entries(settings.policyViolations.checks)
          .filter(([key]) => !LEGACY_CHECK_KEYS.includes(key))
          .map(([key, value]) => [
            key,
            {
              enabled: value.enabled,
              severity: value.severity,
              threshold: value.threshold,
              enforcement: value.enforcement,
            }
          ])
      )
    : undefined;

  return {
    deadline: toTimesheetDeadlineSettingsDto(settings.deadline),
    hoursLimits: toHoursLimitSettingsDto(settings.hoursLimits),
    utilization: toUtilisationSettingsDto(settings.utilisation),
    policyViolations: {
      monitoringFrequency: settings.policyViolations?.monitoringFrequency ?? 'week',
      suppressDailyWhenWeeklyExceeded: settings.policyViolations?.suppressDailyWhenWeeklyExceeded ?? true,
      consolidateDailyWarnings: settings.policyViolations?.consolidateDailyWarnings ?? true,
      checks: checksDto,
      displayMode: settings.policyViolations?.displayMode ?? 'smart',
      showInfoAlerts: settings.policyViolations?.showInfoAlerts ?? true,
      preset: settings.policyViolations?.preset ?? 'balanced',
    },
    policyLimits: settings.policyLimits ? {
      legalMaxHoursPerDay: settings.policyLimits.legalMaxHoursPerDay,
      legalMaxHoursPerWeek: settings.policyLimits.legalMaxHoursPerWeek,
    } : undefined,
    weekStartDay: settings.weekStartDay,
    showRole: settings.showRole,
    requireRole: settings.requireRole,
    showCostCentre: settings.showCostCentre,
    requireCostCentre: settings.requireCostCentre,
    allowWeekendEntry: false,
    timezoneId: "UTC",
    updatedAt: new Date().toISOString(),
    updatedBy: null,
    rowVersion: rowVersion || '',
  };
}

/**
 * Convert backend OrganisationTimesheetSettingsDto to frontend
 * Note: workingProfile is fetched separately from OrganisationWorkingPattern API
 * and merged in settingsRepository.getOrganisationTimesheetSettings()
 */
export function fromOrganisationTimesheetSettingsDto(
  dto: OrganisationTimesheetSettingsDto
): OrganisationTimesheetSettings & { rowVersion: string } {
  // Convert policy violation checks from DTO format to frontend format
  const checksFromDto = dto.policyViolations?.checks;
  const defaultChecks = DEFAULT_TIMESHEET_SETTINGS.policyViolations.checks;
  let checks: PolicyViolationSettings['checks'] = defaultChecks;

  if (checksFromDto) {
    const parsedChecks = Object.fromEntries(
      Object.entries(checksFromDto).map(([key, value]) => [
        key as PolicyCheckType,
        {
          enabled: value.enabled,
          severity: value.severity as 'critical' | 'warning' | 'info',
          threshold: value.threshold ?? undefined,
          enforcement: (value.enforcement as 'warn' | 'block' | null | undefined) ?? undefined,
        } satisfies PolicyCheckConfig,
      ])
    ) as Partial<Record<PolicyCheckType, PolicyCheckConfig>>;

    const mergedChecks: PolicyViolationSettings['checks'] = {
      ...defaultChecks,
      ...(parsedChecks as Partial<PolicyViolationSettings['checks']>),
    };

    // Migrate legacy keys to mode-prefixed keys (backward compat for old DB records)
    const legacyMissingHours = parsedChecks['missing-hours'];
    if (legacyMissingHours) {
      if (!parsedChecks['daily-missing-hours']) mergedChecks['daily-missing-hours'] = legacyMissingHours;
      if (!parsedChecks['weekly-missing-hours']) mergedChecks['weekly-missing-hours'] = legacyMissingHours;
    }

    const legacyNonWorkingDay = parsedChecks['non-working-day'];
    if (legacyNonWorkingDay) {
      if (!parsedChecks['daily-non-working-day']) mergedChecks['daily-non-working-day'] = legacyNonWorkingDay;
      if (!parsedChecks['weekly-non-working-day']) mergedChecks['weekly-non-working-day'] = legacyNonWorkingDay;
    }

    checks = mergedChecks;
  }

  // Convert policy limits from DTO
  const policyLimits: PolicyLimits | undefined = dto.policyLimits
    ? {
        legalMaxHoursPerDay: dto.policyLimits.legalMaxHoursPerDay,
        legalMaxHoursPerWeek: dto.policyLimits.legalMaxHoursPerWeek,
      }
    : DEFAULT_TIMESHEET_SETTINGS.policyLimits;

  return {
    deadline: fromTimesheetDeadlineSettingsDto(dto.deadline),
    workingProfile: DEFAULT_TIMESHEET_SETTINGS.workingProfile,
    policyLimits,
    hoursLimits: fromHoursLimitSettingsDto(dto.hoursLimits),
    utilisation: fromUtilisationSettingsDto(dto.utilization),
    policyViolations: {
      monitoringFrequency: (dto.policyViolations?.monitoringFrequency as 'day' | 'week') ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.monitoringFrequency,
      suppressDailyWhenWeeklyExceeded: dto.policyViolations?.suppressDailyWhenWeeklyExceeded ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.suppressDailyWhenWeeklyExceeded,
      consolidateDailyWarnings: dto.policyViolations?.consolidateDailyWarnings ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.consolidateDailyWarnings,
      checks,
      displayMode: (dto.policyViolations?.displayMode as 'smart' | 'all' | 'critical-only') ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.displayMode,
      showInfoAlerts: dto.policyViolations?.showInfoAlerts ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.showInfoAlerts,
      preset: (dto.policyViolations?.preset as 'strict' | 'balanced' | 'lenient' | 'custom') ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.preset,
    },
    weekStartDay: dto.weekStartDay,
    showRole: dto.showRole ?? DEFAULT_TIMESHEET_SETTINGS.showRole,
    requireRole: dto.requireRole ?? DEFAULT_TIMESHEET_SETTINGS.requireRole,
    showCostCentre: dto.showCostCentre ?? DEFAULT_TIMESHEET_SETTINGS.showCostCentre,
    requireCostCentre: dto.requireCostCentre ?? DEFAULT_TIMESHEET_SETTINGS.requireCostCentre,
    // New frontend-only settings with defaults (not provided by backend DTO)
    requireDescription: DEFAULT_TIMESHEET_SETTINGS.requireDescription,
    minimumEntryDuration: DEFAULT_TIMESHEET_SETTINGS.minimumEntryDuration,
    timeRounding: DEFAULT_TIMESHEET_SETTINGS.timeRounding,
    allowNegativeHours: DEFAULT_TIMESHEET_SETTINGS.allowNegativeHours,
    historicalEntryLimit: DEFAULT_TIMESHEET_SETTINGS.historicalEntryLimit,
    allowFutureDatedEntries: DEFAULT_TIMESHEET_SETTINGS.allowFutureDatedEntries,
    projectSelectionScope: DEFAULT_TIMESHEET_SETTINGS.projectSelectionScope,
    showArchivedProjects: DEFAULT_TIMESHEET_SETTINGS.showArchivedProjects,
    showProjectTimeRemaining: DEFAULT_TIMESHEET_SETTINGS.showProjectTimeRemaining,
    updatedAt:dto.updatedAt && dto.updatedAt!=="" ? new Date(dto.updatedAt) : new Date(),
    rowVersion: dto.rowVersion ?? '',
  };
}
